import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format, parseISO, subDays } from 'date-fns'
import { fr } from 'date-fns/locale'
import { ChevronDown, Eye, FileSearch, Plus, RefreshCw, Search, Sparkles, X } from 'lucide-react'
import { jsPDF } from 'jspdf'
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import toast from 'react-hot-toast'

import DocumentPreviewDialog from '@/components/DocumentPreviewDialog'
import { useAuth } from '@/context/AuthContext'
import { resolveMedicalDocumentUrl } from '@/lib/medical-document-storage'
import { supabase } from '@/lib/supabase'
import {
  buildLabResultsMatrix,
  buildLabTrendsDataFromResults,
  calculateTestTrend,
  createManualLabResult,
  createManualLabResultBatch,
  formatLabValue,
  getAvailableLabCategories,
  getLabResults,
  getLabValueColor,
  LAB_DISEASE_PROFILES,
  matchesLabDiseaseProfile,
  summarizeLabUnitInsights
} from '@/lib/lab-results'
import { getPatientMedicationHistory } from '@/lib/medications'
import { backfillMedicalDocumentAnalysis } from '@/lib/working-simple-upload'
import type { LabResult, LabTestCategory, LabTrendData, MedicationHistory } from '@/types/health'

type ChartMode = 'absolute' | 'indexed'
type FilterMenuKey = 'disease' | 'specialty' | 'molecules'

interface MedicationTimelineEvent {
  id: string
  date: string
  label: string
}

interface ChartSeriesConfig {
  key: string
  label: string
  test_name: string
  unit: string
  color: string
}

interface TestGroup {
  category: string
  tests: string[]
}

interface PreviewDocumentState {
  title: string
  subtitle?: string | null
  documentId?: string | null
  fileUrl?: string | null
}

interface ManualLabEntryFormState {
  test_date: string
  test_name: string
  result_value: string
  unit: string
  reference_min: string
  reference_max: string
  reference_range: string
  doctor_name: string
  laboratory_name: string
  specialty: string
  notes: string
}

type LabPriorityLevel = 'high' | 'moderate' | 'watch'

interface LabPriorityGroup {
  id: LabPriorityLevel
  label: string
  description: string
  toneClass: string
  badgeClass: string
  results: LabResult[]
}

type MedicationHistoryRow = MedicationHistory & {
  medication?: {
    name?: string | null
  } | null
}

const CHART_COLORS = ['#38bdf8', '#22c55e', '#f59e0b', '#f97316', '#a78bfa', '#ec4899', '#14b8a6', '#ef4444']

const initialManualLabEntryForm = (todayIsoDate: string): ManualLabEntryFormState => ({
  test_date: todayIsoDate,
  test_name: '',
  result_value: '',
  unit: '',
  reference_min: '',
  reference_max: '',
  reference_range: '',
  doctor_name: '',
  laboratory_name: '',
  specialty: '',
  notes: ''
})

function hasManualLabCoreFields(entry: ManualLabEntryFormState) {
  return entry.test_name.trim().length > 0 && entry.result_value.trim().length > 0
}

function formatQueuedManualLabEntry(entry: ManualLabEntryFormState) {
  const parts = [
    entry.test_name.trim(),
    entry.result_value.trim() ? `${entry.result_value.trim()}${entry.unit.trim() ? ` ${entry.unit.trim()}` : ''}` : '',
    entry.reference_range.trim() || [entry.reference_min.trim(), entry.reference_max.trim()].filter(Boolean).join(' - ')
  ].filter(Boolean)

  return parts.join(' · ')
}

function formatFriendlyDate(date: string) {
  return format(parseISO(date), 'dd MMM yyyy', { locale: fr })
}

function normalizeFilterText(value: string | null | undefined): string {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function getCellTone(result: LabResult) {
  if (result.abnormal_flag === 'HH' || result.abnormal_flag === 'LL') {
    return 'border-red-500/40 bg-red-500/10'
  }

  if (result.is_abnormal) {
    return 'border-orange-500/40 bg-orange-500/10'
  }

  return 'border-emerald-500/30 bg-emerald-500/10'
}

function formatReferenceRange(result: LabResult): string {
  const rawReferenceRange = (result as LabResult & { raw_reference_range?: string | null }).raw_reference_range

  if (rawReferenceRange) {
    return rawReferenceRange
  }

  if (result.reference_min !== null && result.reference_max !== null) {
    return `${result.reference_min} - ${result.reference_max}${result.unit ? ` ${result.unit}` : ''}`
  }

  if (result.reference_max !== null) {
    return `< ${result.reference_max}${result.unit ? ` ${result.unit}` : ''}`
  }

  if (result.reference_min !== null) {
    return `> ${result.reference_min}${result.unit ? ` ${result.unit}` : ''}`
  }

  return 'Non renseignee'
}

function formatComparableReferenceRange(result: LabResult): string {
  const referenceMin = result.normalized_reference_min ?? result.reference_min
  const referenceMax = result.normalized_reference_max ?? result.reference_max
  const unit = result.normalized_unit || result.unit

  if (referenceMin !== null && referenceMax !== null) {
    return `${referenceMin} - ${referenceMax}${unit ? ` ${unit}` : ''}`
  }

  if (referenceMax !== null) {
    return `< ${referenceMax}${unit ? ` ${unit}` : ''}`
  }

  if (referenceMin !== null) {
    return `> ${referenceMin}${unit ? ` ${unit}` : ''}`
  }

  return 'Non renseignee'
}

function formatWindowLabel(days: number, totalDays: number) {
  if (days >= totalDays) {
    return 'Tout l historique'
  }

  if (days <= 31) {
    return `${days} jour${days > 1 ? 's' : ''}`
  }

  if (days < 365) {
    const months = Math.round((days / 30) * 10) / 10
    return `${months} mois`
  }

  const years = Math.round((days / 365) * 10) / 10
  return `${years} an${years > 1 ? 's' : ''}`
}

function getInclusiveDateDistance(startDate: string, endDate: string) {
  const start = new Date(`${startDate}T00:00:00`).getTime()
  const end = new Date(`${endDate}T00:00:00`).getTime()

  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return null
  }

  return Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1)
}

function clampDateInput(date: string, minDate: string | null, maxDate: string) {
  if (!date) {
    return ''
  }

  if (minDate && date < minDate) {
    return minDate
  }

  if (date > maxDate) {
    return maxDate
  }

  return date
}

function buildSpecialtyOptions(results: LabResult[]): string[] {
  const specialties = new Map<string, string>()

  results.forEach((result) => {
    if (!result.specialty) {
      return
    }

    const trimmedSpecialty = result.specialty.trim()
    if (!trimmedSpecialty) {
      return
    }

    const normalizedKey = normalizeFilterText(trimmedSpecialty)
    if (!specialties.has(normalizedKey)) {
      specialties.set(normalizedKey, trimmedSpecialty)
    }
  })

  return [...specialties.values()].sort((left, right) => left.localeCompare(right, 'fr'))
}

function matchesSelectedSpecialties(result: LabResult, selectedSpecialties: string[]) {
  if (selectedSpecialties.length === 0) {
    return true
  }

  const resultSpecialty = normalizeFilterText(result.specialty)
  return selectedSpecialties.some((specialty) => normalizeFilterText(specialty) === resultSpecialty)
}

function matchesSelectedDiseaseProfiles(result: LabResult, selectedProfiles: typeof LAB_DISEASE_PROFILES) {
  if (selectedProfiles.length === 0) {
    return true
  }

  return selectedProfiles.some((profile) => matchesLabDiseaseProfile(result, profile))
}

function formatMultiSelectLabel(baseLabel: string, selectedLabels: string[]) {
  if (selectedLabels.length === 0) {
    return baseLabel
  }

  if (selectedLabels.length === 1) {
    return selectedLabels[0]
  }

  return `${baseLabel} · ${selectedLabels.length}`
}

function formatSelectedTestsLabel(selectedTests: string[], selectionSource: 'manual' | 'profile' | 'auto') {
  if (selectedTests.length === 0) {
    return 'Molécules'
  }

  if (selectionSource === 'manual' && selectedTests.length <= 2) {
    return selectedTests.join(', ')
  }

  if (selectionSource === 'profile') {
    return `Profil · ${selectedTests.length}`
  }

  if (selectionSource === 'auto') {
    return `Auto · ${selectedTests.length}`
  }

  return `${selectedTests.length} molécules`
}

function formatMoleculeMenuLabel(input: {
  selectedCategory: string
  selectedTests: string[]
  effectiveSelectedTests: string[]
  isProfileSelection: boolean
}) {
  if (input.selectedTests.length === 1) {
    return input.selectedTests[0]
  }

  if (input.selectedCategory) {
    const count =
      input.selectedTests.length > 0
        ? input.selectedTests.length
        : input.effectiveSelectedTests.length

    return `${input.selectedCategory} · ${count}`
  }

  if (input.selectedTests.length > 1) {
    return `Molecules · ${input.selectedTests.length}`
  }

  if (input.isProfileSelection && input.effectiveSelectedTests.length > 0) {
    return `Profil · ${input.effectiveSelectedTests.length}`
  }

  return 'Familles / molecules'
}

function getTopTests(results: LabResult[], limit = 8): string[] {
  const usage = new Map<string, number>()

  results.forEach((result) => {
    usage.set(result.test_name, (usage.get(result.test_name) || 0) + 1)
  })

  return [...usage.entries()]
    .sort((left, right) => {
      if (right[1] !== left[1]) {
        return right[1] - left[1]
      }

      return left[0].localeCompare(right[0], 'fr')
    })
    .slice(0, limit)
    .map(([testName]) => testName)
}

function buildTestGroups(results: LabResult[], searchTerm: string): TestGroup[] {
  const normalizedSearch = normalizeFilterText(searchTerm)
  const grouped = new Map<string, Set<string>>()

  results.forEach((result) => {
    if (normalizedSearch && !normalizeFilterText(result.test_name).includes(normalizedSearch)) {
      return
    }

    const category = result.test_category || 'Autre'

    if (!grouped.has(category)) {
      grouped.set(category, new Set())
    }

    grouped.get(category)!.add(result.test_name)
  })

  return [...grouped.entries()]
    .map(([category, tests]) => ({
      category,
      tests: [...tests].sort((left, right) => left.localeCompare(right, 'fr'))
    }))
    .filter((group) => group.tests.length > 0)
    .sort((left, right) => left.category.localeCompare(right.category, 'fr'))
}

function formatTrendTick(date: string, totalDays: number) {
  if (totalDays > 730) return format(parseISO(date), 'MM/yy')
  if (totalDays > 120) return format(parseISO(date), 'MMM yy', { locale: fr })
  if (totalDays > 35) return format(parseISO(date), 'dd MMM', { locale: fr })
  return format(parseISO(date), 'dd/MM')
}

function formatTrendStateLabel(trend: ReturnType<typeof calculateTestTrend>) {
  switch (trend) {
    case 'improving':
      return 'Amelioration'
    case 'worsening':
      return 'Aggravation'
    case 'stable':
      return 'Stable'
    default:
      return 'A confirmer'
  }
}

function sanitizeFileNamePart(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function escapeCsvCell(value: string | number | null | undefined) {
  const safeValue = value === null || value === undefined ? '' : String(value)
  return `"${safeValue.replace(/"/g, '""')}"`
}

function escapeHtml(value: string | number | null | undefined) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function getComparableResultValue(result: LabResult): number | null {
  return result.normalized_value ?? result.result_value
}

function getComparableResultUnit(result: LabResult): string | null {
  return result.normalized_unit || result.unit || null
}

function formatComparableResultValue(result: LabResult) {
  return formatLabValue(
    getComparableResultValue(result),
    getComparableResultUnit(result),
    result.result_text
  )
}

function getPriorityRank(level: LabPriorityLevel) {
  if (level === 'high') return 0
  if (level === 'moderate') return 1
  return 2
}

function getLabPriorityLevel(result: LabResult): LabPriorityLevel {
  if (result.abnormal_flag === 'HH' || result.abnormal_flag === 'LL') {
    return 'high'
  }

  const normalizedName = normalizeFilterText(result.test_name)
  const normalizedCategory = normalizeFilterText(result.test_category || '')

  if (
    normalizedCategory === 'inflammation' ||
    normalizedCategory === 'rein' ||
    normalizedCategory === 'foie' ||
    /crp|calprotectine|creatinine|dfg|albumine|hemoglobine|leucocytes|plaquettes/.test(normalizedName)
  ) {
    return 'high'
  }

  if (
    normalizedCategory === 'fer' ||
    normalizedCategory === 'hematologie' ||
    normalizedCategory === 'metabolisme' ||
    normalizedCategory === 'thyroide' ||
    normalizedCategory === 'hormones' ||
    normalizedCategory === 'immunologie'
  ) {
    return 'moderate'
  }

  return 'watch'
}

function buildPriorityGroups(results: LabResult[]): LabPriorityGroup[] {
  const latestAbnormalByTest = new Map<string, LabResult>()

  results.forEach((result) => {
    if (!result.is_abnormal) {
      return
    }

    const key = `${result.test_name}::${result.unit_group_key || result.unit || 'no-unit'}`
    const existing = latestAbnormalByTest.get(key)

    if (!existing || result.test_date > existing.test_date) {
      latestAbnormalByTest.set(key, result)
    }
  })

  const abnormalResults = [...latestAbnormalByTest.values()].sort((left, right) => {
    const rankDiff = getPriorityRank(getLabPriorityLevel(left)) - getPriorityRank(getLabPriorityLevel(right))
    if (rankDiff !== 0) {
      return rankDiff
    }

    return right.test_date.localeCompare(left.test_date)
  })

  const groups: LabPriorityGroup[] = [
    {
      id: 'high',
      label: 'Priorite haute',
      description: 'Anomalies a discuter en premier.',
      toneClass: 'border-red-500/20 bg-red-500/8',
      badgeClass: 'border-red-500/30 bg-red-500/10 text-red-100',
      results: []
    },
    {
      id: 'moderate',
      label: 'Priorite moderee',
      description: 'Retentissement a surveiller ou recouper.',
      toneClass: 'border-orange-500/20 bg-orange-500/8',
      badgeClass: 'border-orange-500/30 bg-orange-500/10 text-orange-100',
      results: []
    },
    {
      id: 'watch',
      label: 'Surveillance',
      description: 'Anomalies a suivre sans urgence evidente.',
      toneClass: 'border-cyan-500/20 bg-cyan-500/8',
      badgeClass: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-100',
      results: []
    }
  ]

  abnormalResults.forEach((result) => {
    const targetGroup = groups.find((group) => group.id === getLabPriorityLevel(result))
    if (targetGroup) {
      targetGroup.results.push(result)
    }
  })

  return groups
}

function buildVisibleFilterSummary(input: {
  diseaseProfileLabel?: string | null
  categoryLabel?: string | null
  searchTerm?: string
  windowLabel: string
  selectedTests: string[]
  showAbnormalOnly: boolean
}): string[] {
  return [
    input.diseaseProfileLabel ? `Situation: ${input.diseaseProfileLabel}` : null,
    input.categoryLabel ? `Famille: ${input.categoryLabel}` : null,
    `Periode: ${input.windowLabel}`,
    input.showAbnormalOnly ? 'Affichage: anomalies seulement' : null,
    input.searchTerm ? `Recherche: ${input.searchTerm}` : null,
    input.selectedTests.length > 0
      ? input.selectedTests.length <= 5
        ? `Molecules: ${input.selectedTests.join(', ')}`
        : `Molecules: ${input.selectedTests.length} selections`
      : null
  ].filter(Boolean) as string[]
}

function exportLabResultsCsv(
  results: LabResult[],
  options: {
    diseaseProfileLabel?: string | null
    categoryLabel?: string | null
  }
) {
  const header = [
    'date',
    'analyse',
    'famille',
    'valeur',
    'valeur_comparee',
    'unite',
    'unite_comparee',
    'normes',
    'normes_comparees',
    'anormal',
    'drapeau',
    'laboratoire',
    'prescripteur',
    'document',
    'fichier'
  ]

  const rows = results.map((result) => [
    result.test_date,
    result.test_name,
    result.test_category || '',
    result.result_text || result.result_value || '',
    result.normalized_value ?? '',
    result.unit || '',
    result.normalized_unit || '',
    result.reference_range || '',
    formatComparableReferenceRange(result),
    result.is_abnormal ? 'oui' : 'non',
    result.abnormal_flag || '',
    result.laboratory_name || '',
    result.doctor_name || '',
    result.source_document_title || '',
    result.source_file_name || ''
  ])

  const csvContent = [header, ...rows]
    .map((row) => row.map((cell) => escapeCsvCell(cell)).join(';'))
    .join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  const contextParts = [
    options.diseaseProfileLabel ? sanitizeFileNamePart(options.diseaseProfileLabel) : null,
    options.categoryLabel ? sanitizeFileNamePart(options.categoryLabel) : null,
    format(new Date(), 'yyyy-MM-dd')
  ].filter(Boolean)

  anchor.href = url
  anchor.download = `analyses-biologiques-${contextParts.join('-') || 'export'}.csv`
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

function addPdfSection(doc: jsPDF, title: string, items: string[], startY: number): number {
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const marginX = 42
  const lineHeight = 14
  let y = startY

  const ensureSpace = (needed: number) => {
    if (y + needed <= pageHeight - 42) {
      return
    }

    doc.addPage()
    y = 48
  }

  ensureSpace(24)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(12)
  doc.text(title, marginX, y)
  y += 16

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)

  items.forEach((item) => {
    const lines = doc.splitTextToSize(`- ${item}`, pageWidth - marginX * 2)
    ensureSpace(lines.length * lineHeight + 4)
    lines.forEach((line: string) => {
      doc.text(line, marginX, y)
      y += lineHeight
    })
    y += 2
  })

  return y + 6
}

function exportLabSummaryPdf(input: {
  patientName?: string | null
  filterSummary: string[]
  priorityGroups: LabPriorityGroup[]
  selectedResult: LabResult | null
  selectedResultTrend: ReturnType<typeof calculateTestTrend>
  selectedResultHistory: LabResult[]
  results: LabResult[]
}) {
  const doc = new jsPDF({
    unit: 'pt',
    format: 'a4'
  })
  const marginX = 42
  const now = new Date()
  let y = 54

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(18)
  doc.text('Synthese biologique', marginX, y)
  y += 22

  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')

  ;[
    input.patientName ? `Patient: ${input.patientName}` : null,
    `Genere le ${format(now, 'dd/MM/yyyy HH:mm')}`,
    `${input.results.length} resultat(s) visible(s)`
  ]
    .filter(Boolean)
    .forEach((line) => {
      doc.text(String(line), marginX, y)
      y += 14
    })

  if (input.filterSummary.length > 0) {
    y = addPdfSection(doc, 'Filtres actifs', input.filterSummary, y + 4)
  }

  input.priorityGroups
    .filter((group) => group.results.length > 0)
    .forEach((group) => {
      y = addPdfSection(
        doc,
        group.label,
        group.results.slice(0, 8).map((result) =>
          [
            result.test_name,
            formatComparableResultValue(result),
            `le ${formatFriendlyDate(result.test_date)}`,
            result.source_document_title
          ]
            .filter(Boolean)
            .join(' - ')
        ),
        y
      )
    })

  if (input.selectedResult) {
    y = addPdfSection(
      doc,
      `Molecule focus: ${input.selectedResult.test_name}`,
      [
        `Valeur: ${formatComparableResultValue(input.selectedResult)}`,
        `Normes: ${formatComparableReferenceRange(input.selectedResult)}`,
        `Date: ${formatFriendlyDate(input.selectedResult.test_date)}`,
        `Tendance: ${formatTrendStateLabel(input.selectedResultTrend)}`,
        `Occurrences visibles: ${input.selectedResultHistory.length}`
      ],
      y
    )
  }

  y = addPdfSection(
    doc,
    'Resultats visibles',
    input.results.slice(0, 24).map((result) =>
      [
        formatFriendlyDate(result.test_date),
        result.test_name,
        formatComparableResultValue(result),
        result.is_abnormal ? 'anormal' : 'normal'
      ].join(' - ')
    ),
    y
  )

  doc.save(`synthese-biologique-${format(now, 'yyyy-MM-dd')}.pdf`)
}

function printLabSummary(input: {
  patientName?: string | null
  filterSummary: string[]
  priorityGroups: LabPriorityGroup[]
  selectedResult: LabResult | null
  selectedResultTrend: ReturnType<typeof calculateTestTrend>
  selectedResultHistory: LabResult[]
  results: LabResult[]
}) {
  const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=980,height=1200')

  if (!printWindow) {
    throw new Error('Impossible d ouvrir la fenetre d impression')
  }

  const summaryHtml = `
    <html>
      <head>
        <title>Synthese biologique</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 32px; color: #111827; }
          h1, h2, h3 { margin: 0 0 10px 0; }
          h1 { font-size: 24px; }
          h2 { font-size: 16px; margin-top: 24px; }
          p, li, td, th { font-size: 12px; line-height: 1.45; }
          .muted { color: #475569; }
          .meta { margin-bottom: 16px; }
          .chip { display: inline-block; padding: 4px 8px; border: 1px solid #cbd5e1; border-radius: 999px; margin: 0 8px 8px 0; font-size: 11px; }
          .group { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-top: 12px; }
          .grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }
          table { width: 100%; border-collapse: collapse; margin-top: 12px; }
          th, td { border-bottom: 1px solid #e2e8f0; text-align: left; padding: 8px 6px; vertical-align: top; }
          @media print {
            body { margin: 18px; }
            .page-break-avoid { break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <h1>Synthese biologique</h1>
        <div class="meta">
          ${input.patientName ? `<p><strong>Patient:</strong> ${escapeHtml(input.patientName)}</p>` : ''}
          <p class="muted">Genere le ${escapeHtml(format(new Date(), 'dd/MM/yyyy HH:mm'))}</p>
          <p class="muted">${escapeHtml(input.results.length)} resultat(s) visible(s)</p>
        </div>

        ${input.filterSummary.length > 0
          ? `<div class="page-break-avoid"><h2>Filtres actifs</h2>${input.filterSummary.map((item) => `<span class="chip">${escapeHtml(item)}</span>`).join('')}</div>`
          : ''}

        <h2>Anomalies a prioriser</h2>
        <div class="grid">
          ${input.priorityGroups.map((group) => `
            <div class="group page-break-avoid">
              <h3>${escapeHtml(group.label)}</h3>
              <p class="muted">${escapeHtml(group.description)}</p>
              ${group.results.length > 0
                ? `<ul>${group.results.slice(0, 8).map((result) => `
                    <li>
                      <strong>${escapeHtml(result.test_name)}</strong> - ${escapeHtml(formatComparableResultValue(result))}
                      <br />
                      <span class="muted">${escapeHtml(formatFriendlyDate(result.test_date))}${result.source_document_title ? ` - ${escapeHtml(result.source_document_title)}` : ''}</span>
                    </li>
                  `).join('')}</ul>`
                : `<p class="muted">Aucune anomalie dans ce groupe.</p>`}
            </div>
          `).join('')}
        </div>

        ${input.selectedResult
          ? `
            <div class="group page-break-avoid">
              <h2>Molecule focus</h2>
              <p><strong>${escapeHtml(input.selectedResult.test_name)}</strong></p>
              <p>Valeur: ${escapeHtml(formatComparableResultValue(input.selectedResult))}</p>
              <p>Normes: ${escapeHtml(formatComparableReferenceRange(input.selectedResult))}</p>
              <p>Date: ${escapeHtml(formatFriendlyDate(input.selectedResult.test_date))}</p>
              <p>Tendance: ${escapeHtml(formatTrendStateLabel(input.selectedResultTrend))}</p>
              <p>Occurrences visibles: ${escapeHtml(input.selectedResultHistory.length)}</p>
            </div>
          `
          : ''}

        <h2>Resultats visibles</h2>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Analyse</th>
              <th>Valeur</th>
              <th>Normes</th>
              <th>Document</th>
            </tr>
          </thead>
          <tbody>
            ${input.results.slice(0, 40).map((result) => `
              <tr>
                <td>${escapeHtml(formatFriendlyDate(result.test_date))}</td>
                <td>${escapeHtml(result.test_name)}</td>
                <td>${escapeHtml(formatComparableResultValue(result))}</td>
                <td>${escapeHtml(formatComparableReferenceRange(result))}</td>
                <td>${escapeHtml(result.source_document_title || '')}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
    </html>
  `

  printWindow.document.open()
  printWindow.document.write(summaryHtml)
  printWindow.document.close()
  printWindow.focus()
  printWindow.print()
}

function buildMedicationTimelineEvents(history: MedicationHistoryRow[]): MedicationTimelineEvent[] {
  return history
    .filter((entry) => entry.action === 'stopped' || entry.action === 'completed')
    .map((entry) => {
      const medicationName = entry.medication?.name?.trim() || 'Traitement'

      return {
        id: entry.id,
        date: entry.change_date.slice(0, 10),
        label: entry.action === 'completed' ? `Fin ${medicationName}` : `Arret ${medicationName}`
      }
    })
    .sort((left, right) => left.date.localeCompare(right.date))
}

function buildChartSeriesConfig(series: LabTrendData[]): ChartSeriesConfig[] {
  return series.map((entry, index) => ({
    key: `series_${index}`,
    label: entry.series_label || entry.test_name,
    test_name: entry.test_name,
    unit: entry.unit || '',
    color: CHART_COLORS[index % CHART_COLORS.length]
  }))
}

function getChartMode(series: LabTrendData[]): ChartMode {
  const uniqueUnits = [...new Set(series.map((entry) => entry.unit).filter(Boolean))]

  if (series.length <= 1 || uniqueUnits.length <= 1) {
    return 'absolute'
  }

  return 'indexed'
}

function buildMultiSeriesChartData(
  series: LabTrendData[],
  configs: ChartSeriesConfig[],
  mode: ChartMode,
  rangeStartDate?: string | null,
  rangeEndDate?: string | null
): Array<Record<string, number | string | null>> {
  const rowsByDate = new Map<string, Record<string, number | string | null>>()

  series.forEach((trendData, index) => {
    const config = configs[index]
    const baseline = trendData.results[0]?.value || 1

    trendData.results.forEach((result) => {
      const existingRow = rowsByDate.get(result.date) || { date: result.date }
      const plotValue = mode === 'indexed' && baseline !== 0 ? (result.value / baseline) * 100 : result.value

      existingRow[config.key] = plotValue
      existingRow[`actual_${config.key}`] = result.value
      existingRow[`abnormal_${config.key}`] = result.is_abnormal ? '1' : ''

      rowsByDate.set(result.date, existingRow)
    })
  })

  ;[rangeStartDate, rangeEndDate].filter(Boolean).forEach((date) => {
    const dateKey = String(date)

    if (!rowsByDate.has(dateKey)) {
      rowsByDate.set(dateKey, { date: dateKey })
    }
  })

  return [...rowsByDate.values()].sort((left, right) =>
    String(left.date).localeCompare(String(right.date))
  )
}

function ComparisonTooltip({
  active,
  payload,
  label,
  configs
}: {
  active?: boolean
  payload?: Array<{ dataKey?: string; payload?: Record<string, number | string | null> }>
  label?: string
  configs: ChartSeriesConfig[]
}) {
  if (!active || !payload || payload.length === 0 || !label) {
    return null
  }

  return (
    <div className="rounded-xl border border-dark-border bg-slate-950/95 px-3 py-2 shadow-xl">
      <p className="text-xs text-slate-400 mb-2">{formatFriendlyDate(String(label))}</p>
      <div className="space-y-1.5">
        {configs.map((config) => {
          const row = payload[0]?.payload || {}
          const actualValue = row[`actual_${config.key}`]

          if (actualValue === undefined || actualValue === null) {
            return null
          }

          return (
            <div key={config.key} className="flex items-center justify-between gap-4 text-xs">
              <span className="flex items-center gap-2 text-slate-200">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: config.color }} />
                {config.label}
              </span>
              <span className="text-white font-medium">
                {formatLabValue(Number(actualValue), config.unit || null)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function MultiTrendPanel({
  series,
  medicationEvents,
  rangeStartDate,
  rangeEndDate
}: {
  series: LabTrendData[]
  medicationEvents: MedicationTimelineEvent[]
  rangeStartDate?: string | null
  rangeEndDate?: string | null
}) {
  const configs = buildChartSeriesConfig(series)
  const chartMode = getChartMode(series)
  const chartData = buildMultiSeriesChartData(series, configs, chartMode, rangeStartDate, rangeEndDate)
  const firstDate = rangeStartDate || (chartData[0]?.date ? String(chartData[0].date) : null)
  const lastDate = rangeEndDate || (chartData[chartData.length - 1]?.date ? String(chartData[chartData.length - 1].date) : null)
  const totalDays =
    firstDate && lastDate
      ? Math.max(
          1,
          Math.round((new Date(lastDate).getTime() - new Date(firstDate).getTime()) / (1000 * 60 * 60 * 24))
        )
      : 1

  const visibleMedicationEvents = medicationEvents.filter((event) => {
    if (!firstDate || !lastDate) {
      return false
    }

    return event.date >= firstDate && event.date <= lastDate
  })

  return (
    <div className="card relative z-0 overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {configs.map((config) => (
          <span
            key={config.key}
            className="inline-flex items-center gap-2 rounded-full border border-dark-border bg-dark-surface-high px-3 py-1 text-xs text-slate-200"
          >
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: config.color }} />
            {config.label}
          </span>
        ))}
        {chartMode === 'indexed' && (
          <span className="inline-flex items-center rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-xs text-cyan-100">
            Comparatif base 100
          </span>
        )}
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 10 }}>
            <CartesianGrid stroke="#23324c" strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              stroke="#94a3b8"
              fontSize={12}
              minTickGap={24}
              interval="preserveStartEnd"
              tickFormatter={(value) => formatTrendTick(String(value), totalDays)}
            />
            <YAxis
              stroke="#94a3b8"
              fontSize={12}
              width={60}
              tickFormatter={(value) => (chartMode === 'indexed' ? `${Math.round(Number(value))}` : `${value}`)}
            />

            {chartMode === 'indexed' && (
              <ReferenceLine y={100} stroke="#475569" strokeDasharray="4 4" ifOverflow="extendDomain" />
            )}

            {visibleMedicationEvents.map((event) => (
              <ReferenceLine
                key={event.id}
                x={event.date}
                stroke="#ef4444"
                strokeDasharray="5 4"
                ifOverflow="extendDomain"
              />
            ))}

            <Tooltip content={<ComparisonTooltip configs={configs} />} />

            {configs.map((config) => (
              <Line
                key={config.key}
                type="monotone"
                dataKey={config.key}
                stroke={config.color}
                strokeWidth={2.5}
                dot={{ fill: config.color, strokeWidth: 0, r: 3 }}
                activeDot={{ r: 5, fill: '#f8fafc' }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {visibleMedicationEvents.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {visibleMedicationEvents.map((event) => (
            <span
              key={event.id}
              className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1 text-xs text-red-100"
            >
              {formatFriendlyDate(event.date)} - {event.label}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export default function LabResults() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const filterBarRef = useRef<HTMLDivElement | null>(null)

  const [selectedDiseaseProfileIds, setSelectedDiseaseProfileIds] = useState<string[]>([])
  const [selectedCategory, setSelectedCategory] = useState<LabTestCategory | ''>('')
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([])
  const [selectedTests, setSelectedTests] = useState<string[]>([])
  const [testSearchTerm, setTestSearchTerm] = useState('')
  const [timeWindowDays, setTimeWindowDays] = useState<number | null>(null)
  const [manualStartDate, setManualStartDate] = useState('')
  const [manualEndDate, setManualEndDate] = useState('')
  const [showManualEntryForm, setShowManualEntryForm] = useState(false)
  const [manualEntryForm, setManualEntryForm] = useState<ManualLabEntryFormState>(() =>
    initialManualLabEntryForm(format(new Date(), 'yyyy-MM-dd'))
  )
  const [manualEntryBatch, setManualEntryBatch] = useState<ManualLabEntryFormState[]>([])
  const [openMenu, setOpenMenu] = useState<FilterMenuKey | null>(null)
  const [selectedResult, setSelectedResult] = useState<LabResult | null>(null)
  const [previewDocument, setPreviewDocument] = useState<PreviewDocumentState | null>(null)

  const {
    data: allResults = [],
    isLoading,
    isFetching
  } = useQuery({
    queryKey: ['lab-results-canonical', user?.id],
    queryFn: () => getLabResults(user!.id, null),
    enabled: Boolean(user)
  })

  const { data: medicationHistory = [] } = useQuery({
    queryKey: ['patient-medication-history', user?.id],
    queryFn: () => getPatientMedicationHistory(user!.id) as Promise<MedicationHistoryRow[]>,
    enabled: Boolean(user)
  })

  const reanalyzeMutation = useMutation({
    mutationFn: () => backfillMedicalDocumentAnalysis(user!.id),
    onSuccess: (result) => {
      toast.success(`${result.analyzed} document(s) reanalyse(s), ${result.skipped} deja complets`)
      queryClient.invalidateQueries({ queryKey: ['lab-results-canonical', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['analyses-resultats', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['medical-documents', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['documents-with-extracts', user?.id] })
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Impossible de relancer le scan IA')
    }
  })

  const createManualEntryMutation = useMutation({
    mutationFn: async ({ entries }: { entries: ManualLabEntryFormState[] }) => {
      if (!user) {
        throw new Error('Utilisateur non connecte')
      }

      const parseOptionalNumber = (value: string) => {
        const trimmedValue = value.trim().replace(',', '.')
        if (!trimmedValue) {
          return null
        }

        const parsed = Number.parseFloat(trimmedValue)
        return Number.isFinite(parsed) ? parsed : null
      }

      const normalizedEntries = entries.map((entry) => {
        const normalizedTestName = entry.test_name.trim()
        const normalizedValue = entry.result_value.trim()

        if (!normalizedTestName) {
          throw new Error('La molecule est requise')
        }

        if (!normalizedValue) {
          throw new Error('La valeur du resultat est requise')
        }

        return {
          patient_id: user.id,
          test_date: entry.test_date,
          test_name: normalizedTestName,
          result_value: normalizedValue,
          unit: entry.unit.trim() || null,
          reference_range: entry.reference_range.trim() || null,
          reference_min: parseOptionalNumber(entry.reference_min),
          reference_max: parseOptionalNumber(entry.reference_max),
          doctor_name: entry.doctor_name.trim() || null,
          laboratory_name: entry.laboratory_name.trim() || null,
          specialty: entry.specialty.trim() || null,
          notes: entry.notes.trim() || null
        }
      })

      if (normalizedEntries.length === 1) {
        return [await createManualLabResult(normalizedEntries[0])]
      }

      return createManualLabResultBatch(normalizedEntries)
    },
    onSuccess: (results) => {
      toast.success(
        results.length > 1
          ? `${results.length} resultats biologiques ajoutes`
          : 'Resultat biologique ajoute'
      )
      queryClient.invalidateQueries({ queryKey: ['lab-results-canonical', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['analyses-resultats', user?.id] })
      setShowManualEntryForm(true)
      const latestResult = results[results.length - 1]
      if (latestResult) {
        setSelectedResult(latestResult)
      }
      setSelectedTests((currentTests) =>
        [
          ...currentTests,
          ...results.map((result) => result.test_name)
        ].filter((testName, index, array) => array.indexOf(testName) === index)
      )
      setManualEntryBatch([])
      setManualEntryForm((current) => ({
        ...initialManualLabEntryForm(current.test_date || todayIsoDate),
        test_date: current.test_date,
        doctor_name: current.doctor_name,
        laboratory_name: current.laboratory_name,
        specialty: current.specialty
      }))
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Impossible d ajouter cette analyse')
    }
  })

  const selectedDiseaseProfiles = LAB_DISEASE_PROFILES.filter((profile) =>
    selectedDiseaseProfileIds.includes(profile.id)
  )

  const earliestDate = allResults[allResults.length - 1]?.test_date || allResults[0]?.test_date || null
  const todayDate = new Date()
  const todayIsoDate = format(todayDate, 'yyyy-MM-dd')
  const totalSpanDays =
    earliestDate
      ? Math.max(
          7,
          Math.round((todayDate.getTime() - new Date(earliestDate).getTime()) / (1000 * 60 * 60 * 24))
        )
      : 30

  useEffect(() => {
    if (timeWindowDays === null && totalSpanDays > 0) {
      setTimeWindowDays(totalSpanDays)
    }
  }, [timeWindowDays, totalSpanDays])

  const effectiveWindowDays = Math.min(timeWindowDays || totalSpanDays, totalSpanDays)
  const sliderValue = Math.max(1, totalSpanDays - effectiveWindowDays + 1)
  const sliderWindowStartDate =
    !earliestDate || effectiveWindowDays >= totalSpanDays
      ? null
      : format(subDays(todayDate, Math.max(0, effectiveWindowDays - 1)), 'yyyy-MM-dd')
  const rangeStartDate = manualStartDate || sliderWindowStartDate || earliestDate || todayIsoDate
  const rangeEndDate = manualEndDate || todayIsoDate
  const periodStartLabel = rangeStartDate
  const manualRangeActive = Boolean(manualStartDate || manualEndDate)

  const resultsInWindow = allResults.filter((result) => {
    if (result.test_date > todayIsoDate) {
      return false
    }

    if (rangeStartDate && result.test_date < rangeStartDate) {
      return false
    }

    if (rangeEndDate && result.test_date > rangeEndDate) {
      return false
    }

    return true
  })

  const resultsBySituationFilters = resultsInWindow.filter((result) => {
    return matchesSelectedDiseaseProfiles(result, selectedDiseaseProfiles)
  })

  const availableSpecialties = buildSpecialtyOptions(resultsBySituationFilters)

  const resultsByPrimaryFilters = resultsBySituationFilters.filter((result) => {
    return matchesSelectedSpecialties(result, selectedSpecialties)
  })

  const availableCategories = getAvailableLabCategories(resultsByPrimaryFilters)

  const structurallyFilteredResults = resultsByPrimaryFilters.filter((result) => {
    if (selectedCategory && result.test_category !== selectedCategory) {
      return false
    }

    return true
  })

  const availableTests = [...new Set(structurallyFilteredResults.map((result) => result.test_name))]
    .sort((left, right) => left.localeCompare(right, 'fr'))
  const knownTestNames = [...new Set(allResults.map((result) => result.test_name))]
    .sort((left, right) => left.localeCompare(right, 'fr'))
  const knownDoctorNames = [...new Set(allResults.map((result) => result.doctor_name).filter(Boolean))]
    .sort((left, right) => left!.localeCompare(right!, 'fr')) as string[]
  const knownLaboratoryNames = [...new Set(allResults.map((result) => result.laboratory_name).filter(Boolean))]
    .sort((left, right) => left!.localeCompare(right!, 'fr')) as string[]
  const knownSpecialties = [...new Set(allResults.map((result) => result.specialty).filter(Boolean))]
    .sort((left, right) => left!.localeCompare(right!, 'fr')) as string[]

  const defaultProfileTests =
    selectedDiseaseProfiles.length > 0
      ? availableTests.filter((testName) =>
          selectedDiseaseProfiles.some((profile) => profile.tests.includes(testName))
        )
      : []
  const defaultTopTests = getTopTests(structurallyFilteredResults, 8)
  const manualSelectedTests = selectedTests.filter((testName) => availableTests.includes(testName))

  const effectiveSelectedTests =
    manualSelectedTests.length > 0
      ? manualSelectedTests
      : selectedCategory
        ? availableTests
      : defaultProfileTests.length > 0
        ? defaultProfileTests
        : defaultTopTests

  const selectedTestsLabel = formatMoleculeMenuLabel({
    selectedCategory,
    selectedTests: manualSelectedTests,
    effectiveSelectedTests,
    isProfileSelection: manualSelectedTests.length === 0 && !selectedCategory && defaultProfileTests.length > 0
  })
  const selectedDiseaseProfilesLabel = formatMultiSelectLabel(
    'Situation clinique',
    selectedDiseaseProfiles.map((profile) => profile.label)
  )
  const selectedSpecialtiesLabel = formatMultiSelectLabel('Specialite', selectedSpecialties)

  const filteredResults = structurallyFilteredResults.filter((result) => {
    return effectiveSelectedTests.length === 0 || effectiveSelectedTests.includes(result.test_name)
  })

  const matrix = buildLabResultsMatrix(filteredResults)
  const trendSeries = buildLabTrendsDataFromResults(filteredResults, effectiveSelectedTests)
  const medicationEvents = buildMedicationTimelineEvents(medicationHistory)
  const allTestGroups = buildTestGroups(resultsByPrimaryFilters, '')
  const visibleTestGroups = buildTestGroups(
    selectedCategory ? structurallyFilteredResults : resultsByPrimaryFilters,
    testSearchTerm
  )
  const selectedCategoryTests =
    selectedCategory
      ? visibleTestGroups.find((group) => group.category === selectedCategory)?.tests || []
      : []
  const unitInsight = summarizeLabUnitInsights(structurallyFilteredResults)
  const selectedUnitMixWarning = selectedResult
    ? unitInsight.mixed_unit_tests.find((entry) => entry.test_name === selectedResult.test_name) || null
    : null
  const selectedResultHistory = selectedResult
    ? filteredResults
        .filter((result) => {
          if (result.test_name !== selectedResult.test_name) {
            return false
          }

          if (selectedResult.unit_group_key && result.unit_group_key) {
            return result.unit_group_key === selectedResult.unit_group_key
          }

          return true
        })
        .slice()
        .sort((left, right) => right.test_date.localeCompare(left.test_date))
    : []
  const selectedResultTrend = selectedResultHistory.length > 1
    ? calculateTestTrend(selectedResultHistory)
    : 'unknown'

  useEffect(() => {
    setSelectedTests([])
  }, [selectedDiseaseProfileIds])

  useEffect(() => {
    setSelectedTests((currentTests) => currentTests.filter((testName) => availableTests.includes(testName)))
  }, [availableTests])

  useEffect(() => {
    if (selectedCategory && !availableCategories.includes(selectedCategory)) {
      setSelectedCategory('')
    }
  }, [availableCategories, selectedCategory])

  useEffect(() => {
    setSelectedSpecialties((currentSpecialties) =>
      currentSpecialties.filter((specialty) =>
        availableSpecialties.some((availableSpecialty) =>
          normalizeFilterText(availableSpecialty) === normalizeFilterText(specialty)
        )
      )
    )
  }, [availableSpecialties])

  useEffect(() => {
    if (selectedDiseaseProfileIds.some((profileId) => !LAB_DISEASE_PROFILES.some((profile) => profile.id === profileId))) {
      setSelectedDiseaseProfileIds((currentProfileIds) =>
        currentProfileIds.filter((profileId) => LAB_DISEASE_PROFILES.some((profile) => profile.id === profileId))
      )
    }
  }, [selectedDiseaseProfileIds])

  useEffect(() => {
    setTestSearchTerm('')
  }, [selectedDiseaseProfileIds, selectedCategory, selectedSpecialties])

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null

      if (target && filterBarRef.current && !filterBarRef.current.contains(target)) {
        setOpenMenu(null)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenMenu(null)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  useEffect(() => {
    if (matrix.filtered_results.length === 0) {
      if (selectedResult) {
        setSelectedResult(null)
      }
      return
    }

    const selectedStillVisible =
      selectedResult && matrix.filtered_results.some((result) => result.id === selectedResult.id)

    if (!selectedStillVisible) {
      setSelectedResult(matrix.filtered_results[0])
    }
  }, [matrix.filtered_results, selectedResult])

  const toggleTest = (testName: string) => {
    setSelectedTests((currentTests) => {
      const baseTests = currentTests.length > 0 ? currentTests : effectiveSelectedTests
      const nextTests = baseTests.includes(testName)
        ? baseTests.filter((entry) => entry !== testName)
        : [...baseTests, testName]

      return [...new Set(nextTests)].filter((entry) => availableTests.includes(entry))
    })
  }

  const toggleMenu = (menuKey: FilterMenuKey) => {
    setOpenMenu((currentMenu) => (currentMenu === menuKey ? null : menuKey))
  }

  const handleSliderWindowChange = (nextSliderValue: number) => {
    setManualStartDate('')
    setManualEndDate('')
    setTimeWindowDays(Math.max(1, totalSpanDays - nextSliderValue + 1))
  }

  const handleManualStartDateChange = (nextDate: string) => {
    const normalizedDate = clampDateInput(nextDate, earliestDate, manualEndDate || todayIsoDate)
    const nextEndDate =
      manualEndDate && normalizedDate && manualEndDate < normalizedDate
        ? normalizedDate
        : manualEndDate

    setManualStartDate(normalizedDate)
    setManualEndDate(nextEndDate)

    const inclusiveDays = getInclusiveDateDistance(
      normalizedDate || earliestDate || todayIsoDate,
      nextEndDate || todayIsoDate
    )

    if (inclusiveDays !== null) {
      setTimeWindowDays(Math.min(totalSpanDays, inclusiveDays))
    }
  }

  const handleManualEndDateChange = (nextDate: string) => {
    const normalizedDate = clampDateInput(nextDate, manualStartDate || earliestDate, todayIsoDate)
    const nextStartDate =
      manualStartDate && normalizedDate && manualStartDate > normalizedDate
        ? normalizedDate
        : manualStartDate

    setManualStartDate(nextStartDate)
    setManualEndDate(normalizedDate)

    const inclusiveDays = getInclusiveDateDistance(
      nextStartDate || earliestDate || todayIsoDate,
      normalizedDate || todayIsoDate
    )

    if (inclusiveDays !== null) {
      setTimeWindowDays(Math.min(totalSpanDays, inclusiveDays))
    }
  }

  const queueCurrentManualEntry = () => {
    if (!hasManualLabCoreFields(manualEntryForm)) {
      toast.error('Renseigner au minimum la molecule et la valeur avant ajout au lot')
      return
    }

    setManualEntryBatch((currentBatch) => [...currentBatch, { ...manualEntryForm }])
    setManualEntryForm((current) => ({
      ...initialManualLabEntryForm(current.test_date || todayIsoDate),
      test_date: current.test_date,
      doctor_name: current.doctor_name,
      laboratory_name: current.laboratory_name,
      specialty: current.specialty
    }))
  }

  const removeManualEntryFromBatch = (indexToRemove: number) => {
    setManualEntryBatch((currentBatch) => currentBatch.filter((_, index) => index !== indexToRemove))
  }

  const saveSingleManualEntry = () => {
    createManualEntryMutation.mutate({ entries: [{ ...manualEntryForm }] })
  }

  const saveManualEntryBatch = () => {
    createManualEntryMutation.mutate({ entries: manualEntryBatch.map((entry) => ({ ...entry })) })
  }

  const openCategoryTests = (category: LabTestCategory) => {
    setSelectedCategory(category)
    setSelectedTests([])
    setTestSearchTerm('')
    setOpenMenu('molecules')
  }

  const clearMoleculeFilters = () => {
    setSelectedCategory('')
    setSelectedTests([])
    setTestSearchTerm('')
  }

  const showAllMolecules = () => {
    setSelectedCategory('')
    setSelectedTests(availableTests)
    setTestSearchTerm('')
  }

  const resetFilters = () => {
    setSelectedDiseaseProfileIds([])
    setSelectedCategory('')
    setSelectedSpecialties([])
    setSelectedTests([])
    setTestSearchTerm('')
    setTimeWindowDays(totalSpanDays)
    setManualStartDate('')
    setManualEndDate('')
    setOpenMenu(null)
  }

  const toggleDiseaseProfile = (profileId: string) => {
    setSelectedDiseaseProfileIds((currentProfileIds) =>
      currentProfileIds.includes(profileId)
        ? currentProfileIds.filter((currentId) => currentId !== profileId)
        : [...currentProfileIds, profileId]
    )
  }

  const toggleSpecialty = (specialty: string) => {
    setSelectedSpecialties((currentSpecialties) =>
      currentSpecialties.includes(specialty)
        ? currentSpecialties.filter((currentSpecialty) => currentSpecialty !== specialty)
        : [...currentSpecialties, specialty]
    )
  }

  const resolveDocumentPreviewUrl = async ({
    documentId,
    fileUrl
  }: Pick<PreviewDocumentState, 'documentId' | 'fileUrl'>) => {
    if (fileUrl) {
      return resolveMedicalDocumentUrl(fileUrl)
    }

    if (!documentId) {
      return null
    }

    const { data, error } = await supabase
      .from('medical_documents')
      .select('id, file_url, storage_path')
      .eq('id', documentId)
      .maybeSingle()

    if (error) {
      console.error('Impossible de retrouver le document source du resultat biologique:', error)
      return null
    }

    return resolveMedicalDocumentUrl(data?.storage_path || data?.file_url || null)
  }

  const openDocumentPreview = async ({
    title,
    subtitle,
    documentId,
    fileUrl
  }: PreviewDocumentState) => {
    const resolvedFileUrl = await resolveDocumentPreviewUrl({
      documentId,
      fileUrl
    })

    if (!resolvedFileUrl) {
      toast.error('Aucun PDF disponible pour ce resultat')
      return
    }

    setPreviewDocument({
      title,
      subtitle,
      fileUrl: resolvedFileUrl
    })
  }

  if (isLoading) {
    return (
      <div className="card text-center py-12">
        <div className="spinner mx-auto" />
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="h-2.5 w-2.5 rounded-full bg-cyan-400" />
          <h1 className="text-2xl font-bold text-white">Analyses biologiques</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setShowManualEntryForm((current) => {
                const nextValue = !current

                if (!current && selectedResult) {
                  setManualEntryForm((form) => ({
                    ...form,
                    test_date: selectedResult.test_date || form.test_date,
                    test_name: selectedResult.test_name || form.test_name,
                    unit: selectedResult.unit || form.unit,
                    reference_min:
                      selectedResult.reference_min !== null ? String(selectedResult.reference_min) : form.reference_min,
                    reference_max:
                      selectedResult.reference_max !== null ? String(selectedResult.reference_max) : form.reference_max,
                    reference_range: selectedResult.raw_reference_range || form.reference_range,
                    doctor_name: selectedResult.doctor_name || form.doctor_name,
                    laboratory_name: selectedResult.laboratory_name || form.laboratory_name,
                    specialty: selectedResult.specialty || form.specialty
                  }))
                }

                return nextValue
              })
            }}
            className="btn-secondary px-3"
            title="Ajouter une analyse manuellement"
            aria-label="Ajouter une analyse manuellement"
          >
            <Plus className="w-4 h-4" />
          </button>

          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['lab-results-canonical', user?.id] })}
            className="btn-secondary px-3"
            disabled={isFetching}
            title="Rafraichir"
            aria-label="Rafraichir"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>

          <button
            onClick={() => reanalyzeMutation.mutate()}
            className="btn-primary px-3"
            disabled={reanalyzeMutation.isPending || !user}
            title="Relancer le scan IA"
            aria-label="Relancer le scan IA"
          >
            <Sparkles className={`w-4 h-4 ${reanalyzeMutation.isPending ? 'animate-pulse' : ''}`} />
          </button>
        </div>
      </div>

      {showManualEntryForm && (
        <div className="card border border-cyan-500/20 bg-slate-950/95 p-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-semibold text-white">Saisie biologique manuelle</p>
              <p className="text-xs text-slate-400">
                Ajout ligne par ligne, meme sans PDF. Le resultat apparait ensuite dans les courbes et tableaux.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setShowManualEntryForm(false)}
              className="btn-ghost self-start text-xs md:self-auto"
            >
              Fermer
            </button>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs uppercase tracking-[0.16em] text-slate-500">Date</label>
              <input
                type="date"
                value={manualEntryForm.test_date}
                onChange={(event) =>
                  setManualEntryForm((current) => ({ ...current, test_date: event.target.value }))
                }
                className="input"
              />
            </div>

            <div className="xl:col-span-2">
              <label className="mb-1 block text-xs uppercase tracking-[0.16em] text-slate-500">Molecule</label>
              <input
                type="text"
                list="lab-known-tests"
                value={manualEntryForm.test_name}
                onChange={(event) =>
                  setManualEntryForm((current) => ({ ...current, test_name: event.target.value }))
                }
                className="input"
                placeholder="CRP, ferritine, hemoglobine..."
              />
              <datalist id="lab-known-tests">
                {knownTestNames.map((testName) => (
                  <option key={testName} value={testName} />
                ))}
              </datalist>
            </div>

            <div>
              <label className="mb-1 block text-xs uppercase tracking-[0.16em] text-slate-500">Specialite</label>
              <input
                type="text"
                list="lab-known-specialties"
                value={manualEntryForm.specialty}
                onChange={(event) =>
                  setManualEntryForm((current) => ({ ...current, specialty: event.target.value }))
                }
                className="input"
                placeholder="Biologie"
              />
              <datalist id="lab-known-specialties">
                {knownSpecialties.map((specialty) => (
                  <option key={specialty} value={specialty} />
                ))}
              </datalist>
            </div>

            <div>
              <label className="mb-1 block text-xs uppercase tracking-[0.16em] text-slate-500">Valeur</label>
              <input
                type="text"
                value={manualEntryForm.result_value}
                onChange={(event) =>
                  setManualEntryForm((current) => ({ ...current, result_value: event.target.value }))
                }
                className="input"
                placeholder="12.4"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs uppercase tracking-[0.16em] text-slate-500">Unite</label>
              <input
                type="text"
                value={manualEntryForm.unit}
                onChange={(event) =>
                  setManualEntryForm((current) => ({ ...current, unit: event.target.value }))
                }
                className="input"
                placeholder="mg/L"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs uppercase tracking-[0.16em] text-slate-500">Norme min</label>
              <input
                type="text"
                value={manualEntryForm.reference_min}
                onChange={(event) =>
                  setManualEntryForm((current) => ({ ...current, reference_min: event.target.value }))
                }
                className="input"
                placeholder="0"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs uppercase tracking-[0.16em] text-slate-500">Norme max</label>
              <input
                type="text"
                value={manualEntryForm.reference_max}
                onChange={(event) =>
                  setManualEntryForm((current) => ({ ...current, reference_max: event.target.value }))
                }
                className="input"
                placeholder="5"
              />
            </div>

            <div className="md:col-span-2">
              <label className="mb-1 block text-xs uppercase tracking-[0.16em] text-slate-500">Norme texte</label>
              <input
                type="text"
                value={manualEntryForm.reference_range}
                onChange={(event) =>
                  setManualEntryForm((current) => ({ ...current, reference_range: event.target.value }))
                }
                className="input"
                placeholder="0 - 5 mg/L"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs uppercase tracking-[0.16em] text-slate-500">Prescripteur</label>
              <input
                type="text"
                list="lab-known-doctors"
                value={manualEntryForm.doctor_name}
                onChange={(event) =>
                  setManualEntryForm((current) => ({ ...current, doctor_name: event.target.value }))
                }
                className="input"
                placeholder="Dr ..."
              />
              <datalist id="lab-known-doctors">
                {knownDoctorNames.map((doctorName) => (
                  <option key={doctorName} value={doctorName} />
                ))}
              </datalist>
            </div>

            <div>
              <label className="mb-1 block text-xs uppercase tracking-[0.16em] text-slate-500">Laboratoire</label>
              <input
                type="text"
                list="lab-known-laboratories"
                value={manualEntryForm.laboratory_name}
                onChange={(event) =>
                  setManualEntryForm((current) => ({ ...current, laboratory_name: event.target.value }))
                }
                className="input"
                placeholder="Laboratoire"
              />
              <datalist id="lab-known-laboratories">
                {knownLaboratoryNames.map((laboratoryName) => (
                  <option key={laboratoryName} value={laboratoryName} />
                ))}
              </datalist>
            </div>

            <div className="md:col-span-2 xl:col-span-4">
              <label className="mb-1 block text-xs uppercase tracking-[0.16em] text-slate-500">Note</label>
              <input
                type="text"
                value={manualEntryForm.notes}
                onChange={(event) =>
                  setManualEntryForm((current) => ({ ...current, notes: event.target.value }))
                }
                className="input"
                placeholder="Contexte, cycle, commentaire..."
              />
            </div>
          </div>

          {manualEntryBatch.length > 0 && (
            <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-900/80 p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-white">
                  Lot en attente · {manualEntryBatch.length} ligne{manualEntryBatch.length > 1 ? 's' : ''}
                </p>
                <button
                  type="button"
                  onClick={() => setManualEntryBatch([])}
                  className="text-xs text-slate-400 transition-colors hover:text-white"
                  disabled={createManualEntryMutation.isPending}
                >
                  Vider le lot
                </button>
              </div>

              <div className="space-y-2">
                {manualEntryBatch.map((entry, index) => (
                  <div
                    key={`${entry.test_date}-${entry.test_name}-${index}`}
                    className="flex items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-950 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm text-white">{formatQueuedManualLabEntry(entry)}</p>
                      <p className="text-xs text-slate-400">
                        {entry.test_date}
                        {entry.doctor_name.trim() ? ` · ${entry.doctor_name.trim()}` : ''}
                        {entry.laboratory_name.trim() ? ` · ${entry.laboratory_name.trim()}` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeManualEntryFromBatch(index)}
                      className="btn-ghost px-2 py-1 text-xs"
                      disabled={createManualEntryMutation.isPending}
                    >
                      Retirer
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() =>
                setManualEntryForm((current) => ({
                  ...initialManualLabEntryForm(current.test_date || todayIsoDate),
                  test_date: current.test_date,
                  doctor_name: current.doctor_name,
                  laboratory_name: current.laboratory_name,
                  specialty: current.specialty
                }))
              }
              className="btn-ghost text-xs"
              disabled={createManualEntryMutation.isPending}
            >
              Vider
            </button>
            <button
              type="button"
              onClick={queueCurrentManualEntry}
              className="btn-secondary"
              disabled={createManualEntryMutation.isPending}
            >
              <Plus className="h-4 w-4" />
              Ajouter au lot
            </button>
            <button
              type="button"
              onClick={saveSingleManualEntry}
              className="btn-primary"
              disabled={createManualEntryMutation.isPending || !user || !hasManualLabCoreFields(manualEntryForm)}
            >
              <Plus className="h-4 w-4" />
              {createManualEntryMutation.isPending ? 'Ajout...' : 'Ajouter la ligne'}
            </button>
            <button
              type="button"
              onClick={saveManualEntryBatch}
              className="btn-primary"
              disabled={createManualEntryMutation.isPending || !user || manualEntryBatch.length === 0}
            >
              <Plus className="h-4 w-4" />
              {createManualEntryMutation.isPending ? 'Enregistrement...' : 'Enregistrer le lot'}
            </button>
          </div>
        </div>
      )}

      <div
        ref={filterBarRef}
        className="card relative z-[70] overflow-visible border border-slate-800 bg-slate-950 px-3 py-2 shadow-2xl shadow-black/30"
      >
        <div className="grid grid-cols-1 gap-2 xl:grid-cols-[minmax(300px,1.4fr)_240px_240px_320px_auto] xl:items-center">
          <div className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-1.5">
            <div className="flex items-center justify-between gap-2 text-[10px]">
              <span className="uppercase tracking-[0.18em] text-slate-400">Periode</span>
              <span className="text-slate-100">
                {manualRangeActive ? 'Dates libres' : formatWindowLabel(effectiveWindowDays, totalSpanDays)}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span className="shrink-0 text-[10px] text-slate-400">
                {periodStartLabel ? formatFriendlyDate(periodStartLabel) : '--'}
              </span>
              <input
                type="range"
                min={1}
                max={totalSpanDays}
                value={sliderValue}
                onChange={(event) => handleSliderWindowChange(Number(event.target.value))}
                className="h-1.5 flex-1 cursor-pointer accent-cyan-500"
              />
              <span className="shrink-0 text-[10px] text-slate-300">
                {formatFriendlyDate(rangeEndDate)}
              </span>
            </div>
            <div className="mt-2 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-2">
              <input
                type="date"
                value={manualStartDate}
                min={earliestDate || undefined}
                max={manualEndDate || todayIsoDate}
                onChange={(event) => handleManualStartDateChange(event.target.value)}
                className="h-8 rounded-lg border border-slate-700 bg-slate-900 px-2 text-xs text-white"
                aria-label="Date de debut"
              />
              <input
                type="date"
                value={manualEndDate}
                min={manualStartDate || earliestDate || undefined}
                max={todayIsoDate}
                onChange={(event) => handleManualEndDateChange(event.target.value)}
                className="h-8 rounded-lg border border-slate-700 bg-slate-900 px-2 text-xs text-white"
                aria-label="Date de fin"
              />
              {(manualStartDate || manualEndDate) && (
                <button
                  type="button"
                  onClick={() => {
                    setManualStartDate('')
                    setManualEndDate('')
                    setTimeWindowDays(totalSpanDays)
                  }}
                  className="inline-flex h-8 items-center justify-center rounded-lg border border-slate-700 bg-slate-900 px-2 text-[11px] text-slate-300 transition-colors hover:text-white"
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          <div className="relative xl:w-[240px]">
            <button
              type="button"
              onClick={() => toggleMenu('disease')}
              aria-expanded={openMenu === 'disease'}
              className={`flex h-10 w-full items-center justify-between rounded-xl border px-3 text-sm transition-colors ${
                openMenu === 'disease'
                  ? 'border-cyan-500/60 bg-slate-900 text-white'
                  : 'border-slate-700 bg-slate-900 text-slate-200'
              }`}
            >
              <span className="truncate">{selectedDiseaseProfilesLabel}</span>
              <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${openMenu === 'disease' ? 'rotate-180' : ''}`} />
            </button>

            {openMenu === 'disease' && (
              <div className="absolute left-0 top-[calc(100%+0.5rem)] z-[120] w-[min(100vw-2rem,24rem)] rounded-2xl border border-slate-700 bg-slate-950 p-4 shadow-2xl shadow-black/70">
                <div className="mb-3 flex items-center justify-between border-b border-slate-800 pb-3">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Situation clinique</span>
                  {selectedDiseaseProfileIds.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedDiseaseProfileIds([])}
                      className="text-xs text-slate-400 transition-colors hover:text-white"
                    >
                      Effacer
                    </button>
                  )}
                </div>

                <div className="space-y-1.5">
                  {LAB_DISEASE_PROFILES.map((profile) => {
                    const isSelected = selectedDiseaseProfileIds.includes(profile.id)

                    return (
                      <label
                        key={profile.id}
                        className={`flex cursor-pointer items-start justify-between gap-3 rounded-xl border px-3 py-2 text-sm transition-colors ${
                          isSelected
                            ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-100'
                            : 'border-slate-700 bg-slate-900 text-slate-200'
                        }`}
                      >
                        <span className="min-w-0">
                          <span className="block truncate">{profile.label}</span>
                        </span>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleDiseaseProfile(profile.id)}
                          className="mt-0.5 h-4 w-4 rounded border-slate-700 bg-slate-950 text-cyan-500"
                        />
                      </label>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="relative xl:w-[240px]">
            <button
              type="button"
              onClick={() => toggleMenu('specialty')}
              aria-expanded={openMenu === 'specialty'}
              className={`flex h-10 w-full items-center justify-between rounded-xl border px-3 text-sm transition-colors ${
                openMenu === 'specialty'
                  ? 'border-cyan-500/60 bg-slate-900 text-white'
                  : 'border-slate-700 bg-slate-900 text-slate-200'
              }`}
            >
              <span className="truncate">{selectedSpecialtiesLabel}</span>
              <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${openMenu === 'specialty' ? 'rotate-180' : ''}`} />
            </button>

            {openMenu === 'specialty' && (
              <div className="absolute left-0 top-[calc(100%+0.5rem)] z-[120] w-[min(100vw-2rem,24rem)] rounded-2xl border border-slate-700 bg-slate-950 p-4 shadow-2xl shadow-black/70">
                <div className="mb-3 flex items-center justify-between border-b border-slate-800 pb-3">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Specialite</span>
                  {selectedSpecialties.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedSpecialties([])}
                      className="text-xs text-slate-400 transition-colors hover:text-white"
                    >
                      Effacer
                    </button>
                  )}
                </div>

                {availableSpecialties.length === 0 ? (
                  <div className="rounded-2xl border border-slate-700 bg-slate-900 px-4 py-4 text-sm text-slate-400">
                    Aucune specialite
                  </div>
                ) : (
                  <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
                    {availableSpecialties.map((specialty) => {
                      const isSelected = selectedSpecialties.includes(specialty)

                      return (
                        <label
                          key={specialty}
                          className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl border px-3 py-2 text-sm transition-colors ${
                            isSelected
                              ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-100'
                              : 'border-slate-700 bg-slate-900 text-slate-200'
                          }`}
                        >
                          <span className="min-w-0 truncate">{specialty}</span>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSpecialty(specialty)}
                            className="h-4 w-4 rounded border-slate-700 bg-slate-950 text-cyan-500"
                          />
                        </label>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="relative xl:w-[320px]">
            <button
              type="button"
              onClick={() => toggleMenu('molecules')}
              aria-expanded={openMenu === 'molecules'}
              className={`flex h-10 w-full items-center justify-between rounded-xl border px-3 text-sm transition-colors ${
                openMenu === 'molecules'
                  ? 'border-cyan-500/60 bg-slate-900 text-white'
                  : 'border-slate-700 bg-slate-900 text-slate-200'
              }`}
            >
              <span className="truncate">{selectedTestsLabel}</span>
              <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${openMenu === 'molecules' ? 'rotate-180' : ''}`} />
            </button>

            {openMenu === 'molecules' && (
              <div className="absolute right-0 top-[calc(100%+0.5rem)] z-[120] w-[min(100vw-2rem,26rem)] rounded-2xl border border-slate-700 bg-slate-950 p-4 shadow-2xl shadow-black/70">
                <div className="mb-3 flex flex-wrap items-center gap-2 border-b border-slate-800 pb-3">
                  {defaultProfileTests.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCategory('')
                        setSelectedTests(defaultProfileTests)
                        setTestSearchTerm('')
                      }}
                      className="btn-secondary text-xs"
                    >
                      Profil
                    </button>
                  )}
                  <button type="button" onClick={showAllMolecules} className="btn-secondary text-xs">
                    Tout
                  </button>
                  {(manualSelectedTests.length > 0 || selectedCategory) && (
                    <button type="button" onClick={clearMoleculeFilters} className="btn-secondary text-xs">
                      <X className="h-3.5 w-3.5" />
                      Effacer
                    </button>
                  )}
                </div>

                {selectedCategory ? (
                  <>
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <button type="button" onClick={clearMoleculeFilters} className="btn-secondary text-xs">
                          Retour
                        </button>
                        <span className="truncate text-sm font-medium text-white">{selectedCategory}</span>
                      </div>
                      {manualSelectedTests.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setSelectedTests([])}
                          className="text-xs text-slate-400 transition-colors hover:text-white"
                        >
                          Toute la famille
                        </button>
                      )}
                    </div>

                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                      <input
                        type="text"
                        value={testSearchTerm}
                        onChange={(event) => setTestSearchTerm(event.target.value)}
                        className="input h-10 border-slate-700 bg-slate-900 pl-10 text-white"
                        placeholder="Chercher une molecule"
                      />
                    </div>

                    <div className="mt-3 max-h-72 space-y-1.5 overflow-y-auto pr-1">
                      {selectedCategoryTests.length === 0 ? (
                        <div className="rounded-2xl border border-slate-700 bg-slate-900 px-4 py-4 text-sm text-slate-400">
                          Aucune molecule
                        </div>
                      ) : (
                        selectedCategoryTests.map((testName) => {
                          const isSelected = effectiveSelectedTests.includes(testName)

                          return (
                            <label
                              key={testName}
                              className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl border px-3 py-2 text-sm transition-colors ${
                                isSelected
                                  ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-100'
                                  : 'border-slate-700 bg-slate-900 text-slate-200'
                              }`}
                            >
                              <span className="min-w-0 truncate">{testName}</span>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleTest(testName)}
                                className="h-4 w-4 rounded border-slate-700 bg-slate-950 text-cyan-500"
                              />
                            </label>
                          )
                        })
                      )}
                    </div>
                  </>
                ) : (
                  <div className="max-h-80 space-y-1.5 overflow-y-auto pr-1">
                    {allTestGroups.length === 0 ? (
                      <div className="rounded-2xl border border-slate-700 bg-slate-900 px-4 py-4 text-sm text-slate-400">
                        Aucune famille
                      </div>
                    ) : (
                      allTestGroups.map((group) => (
                        <button
                          key={group.category}
                          type="button"
                          onClick={() => openCategoryTests(group.category as LabTestCategory)}
                          className="flex w-full items-center justify-between rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-left text-sm text-slate-200 transition-colors hover:border-cyan-500/50 hover:text-white"
                        >
                          <span className="truncate">{group.category}</span>
                          <span className="rounded-full border border-slate-700 px-2 py-0.5 text-[11px] text-slate-400">
                            {group.tests.length}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-3 text-xs text-slate-300 transition-colors hover:text-white"
            title="Effacer les filtres"
            aria-label="Effacer les filtres"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {allResults.length === 0 ? (
        <div className="card text-center py-14">
          <FileSearch className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <button
            onClick={() => reanalyzeMutation.mutate()}
            className="btn-primary mx-auto"
            disabled={reanalyzeMutation.isPending || !user}
          >
            <Sparkles className="w-4 h-4" />
            Reanalyser
          </button>
        </div>
      ) : matrix.filtered_results.length === 0 ? (
        <div className="card text-center py-14">
          <FileSearch className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <p className="mb-4 text-sm text-slate-400">Aucun resultat avec ces filtres.</p>
          <button onClick={resetFilters} className="btn-secondary mx-auto">
            Reinitialiser
          </button>
        </div>
      ) : (
        <>
          {trendSeries.length > 0 ? (
            <MultiTrendPanel
              series={trendSeries}
              medicationEvents={medicationEvents}
              rangeStartDate={rangeStartDate}
              rangeEndDate={rangeEndDate}
            />
          ) : (
            <div className="card py-8 text-center text-sm text-slate-400">
              Aucune courbe exploitable avec cette selection.
            </div>
          )}

          <div className="relative z-0 grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_280px]">
            <div className="card relative z-0 overflow-hidden">
              <div className="flex items-center justify-between gap-3 border-b border-dark-border px-4 py-3">
                <span className="text-sm font-medium text-white">Analyses</span>
                <span className="text-xs text-slate-400">
                  {matrix.rows.length} bilan{matrix.rows.length > 1 ? 's' : ''} · {matrix.filtered_results.length} valeur{matrix.filtered_results.length > 1 ? 's' : ''}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-dark-border">
                      <th className="text-left py-3 pr-4 text-slate-300 min-w-[8rem]">Date</th>
                      <th className="text-left py-3 pr-4 text-slate-300 min-w-[14rem]">Document</th>
                      {matrix.columns.map((column) => (
                        <th key={column} className="text-left py-3 pr-3 text-slate-300 min-w-[9rem]">
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {matrix.rows.map((row) => (
                      <tr key={row.row_id} className="border-b border-dark-border/60 align-top">
                        <td className="py-3 pr-4 text-slate-200 whitespace-nowrap">
                          {formatFriendlyDate(row.test_date)}
                        </td>

                        <td className="py-3 pr-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="truncate text-white font-medium">{row.document_title}</div>
                              {([row.doctor_name, row.specialty, row.laboratory_name].filter(Boolean).join(' · ')) && (
                                <div className="mt-1 truncate text-xs text-slate-400">
                                  {[row.doctor_name, row.specialty, row.laboratory_name].filter(Boolean).join(' · ')}
                                </div>
                              )}
                            </div>
                            {(row.document_id || row.document_url) && (
                              <button
                                type="button"
                                onClick={() =>
                                  void openDocumentPreview({
                                    title: row.document_title,
                                    subtitle: formatFriendlyDate(row.test_date),
                                    documentId: row.document_id,
                                    fileUrl: row.document_url
                                  })
                                }
                                className="rounded-lg border border-dark-border bg-dark-surface p-2 text-slate-400 transition-colors hover:text-white"
                                title="Aperçu du PDF"
                                aria-label={`Aperçu du document ${row.document_title}`}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>

                        {matrix.columns.map((column) => {
                          const cell = row.cells[column]

                          return (
                            <td key={`${row.row_id}-${column}`} className="py-3 pr-3">
                              {cell ? (
                                <button
                                  onClick={() => setSelectedResult(cell.result)}
                                  className={`w-full rounded-xl border px-3 py-2 text-left transition-colors hover:border-primary-500/60 ${getCellTone(cell.result)}`}
                                >
                                  <div
                                    className={`font-medium ${getLabValueColor(
                                      cell.result.result_value,
                                      cell.result.reference_min,
                                      cell.result.reference_max,
                                      cell.result.abnormal_flag
                                    )}`}
                                  >
                                    {formatLabValue(
                                      cell.result.result_value,
                                      cell.result.unit,
                                      cell.result.result_text
                                    )}
                                  </div>
                                </button>
                              ) : (
                                <div className="text-slate-600">--</div>
                              )}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card relative z-0 h-fit">
              {selectedResult ? (
                <div className="space-y-4">
                  <div className={`rounded-2xl border p-4 ${getCellTone(selectedResult)}`}>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm text-slate-300">{selectedResult.test_name}</p>
                      <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                        {selectedResult.test_category || 'Autre'}
                      </span>
                    </div>
                    <p
                      className={`text-2xl font-semibold mt-2 ${getLabValueColor(
                        selectedResult.result_value,
                        selectedResult.reference_min,
                        selectedResult.reference_max,
                        selectedResult.abnormal_flag
                      )}`}
                    >
                      {formatLabValue(
                        selectedResult.result_value,
                        selectedResult.unit,
                        selectedResult.result_text
                      )}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-3 text-sm">
                    {selectedResult.is_unit_converted && (
                      <div className="grid grid-cols-1 gap-3 rounded-2xl border border-dark-border bg-dark-surface-high p-3">
                        <div>
                          <p className="text-slate-500">Valeur comparee</p>
                          <p className="text-slate-200">
                            {formatLabValue(
                              selectedResult.normalized_value,
                              selectedResult.normalized_unit,
                              selectedResult.result_text
                            )}
                          </p>
                        </div>

                        <div>
                          <p className="text-slate-500">Normes comparees</p>
                          <p className="text-slate-200">{formatComparableReferenceRange(selectedResult)}</p>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-3 rounded-2xl border border-dark-border bg-dark-surface-high p-3">
                      <div>
                        <p className="text-slate-500">Date</p>
                        <p className="text-slate-200">{formatFriendlyDate(selectedResult.test_date)}</p>
                      </div>

                      <div>
                        <p className="text-slate-500">Normes</p>
                        <p className="text-slate-200">{formatReferenceRange(selectedResult)}</p>
                      </div>

                      <div>
                        <p className="text-slate-500">Prescripteur</p>
                        <p className="text-slate-200">{selectedResult.doctor_name || 'Non renseigne'}</p>
                      </div>

                      {selectedResult.specialty && (
                        <div>
                          <p className="text-slate-500">Specialite</p>
                          <p className="text-slate-200">{selectedResult.specialty}</p>
                        </div>
                      )}

                      <div>
                        <p className="text-slate-500">Laboratoire</p>
                        <p className="text-slate-200">{selectedResult.laboratory_name || 'Non renseigne'}</p>
                      </div>

                      <div>
                        <p className="text-slate-500">Document</p>
                        <p className="text-slate-200">{selectedResult.source_document_title}</p>
                      </div>

                      <div>
                        <p className="text-slate-500">Tendance</p>
                        <p className="text-slate-200">{formatTrendStateLabel(selectedResultTrend)}</p>
                      </div>
                    </div>

                    {(selectedResult.unit_warning || selectedUnitMixWarning) && (
                      <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-sm text-slate-200">
                        {selectedResult.unit_warning || null}
                        {selectedResult.unit_warning && selectedUnitMixWarning ? ' ' : ''}
                        {selectedUnitMixWarning
                          ? `Cette molecule existe avec plusieurs unites (${selectedUnitMixWarning.unit_labels.join(' / ')}). Les courbes sont volontairement separees.`
                          : null}
                      </div>
                    )}

                    {selectedResultHistory.length > 0 && (
                      <div>
                        <p className="text-slate-500">Historique recent · {selectedResultHistory.length}</p>
                        <div className="mt-2 space-y-2">
                          {selectedResultHistory.slice(0, 5).map((result) => (
                            <div
                              key={result.id}
                              className="rounded-xl border border-dark-border bg-dark-surface-high px-3 py-2"
                            >
                              <div className="flex items-center justify-between gap-3">
                                <span className="text-slate-300">{formatFriendlyDate(result.test_date)}</span>
                                <span
                                  className={`font-medium ${getLabValueColor(
                                    result.result_value,
                                    result.reference_min,
                                    result.reference_max,
                                    result.abnormal_flag
                                  )}`}
                                >
                                  {formatLabValue(
                                    result.result_value,
                                    result.unit,
                                    result.result_text
                                  )}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(selectedResult.document_id || selectedResult.document_url) && (
                      <button
                        type="button"
                        onClick={() =>
                          void openDocumentPreview({
                            title: selectedResult.source_document_title,
                            subtitle: `${formatFriendlyDate(selectedResult.test_date)} · ${selectedResult.test_name}`,
                            documentId: selectedResult.document_id,
                            fileUrl: selectedResult.document_url
                          })
                        }
                        className="btn-secondary w-full"
                      >
                        <Eye className="h-4 w-4" />
                        Aperçu du PDF
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-slate-400">Selectionne une cellule.</p>
              )}
            </div>
          </div>
        </>
      )}

      {previewDocument && (
        <DocumentPreviewDialog
          title={previewDocument.title}
          subtitle={previewDocument.subtitle}
          fileUrl={previewDocument.fileUrl}
          onClose={() => setPreviewDocument(null)}
        />
      )}
    </div>
  )
}
